import {useQueryClient} from '@tanstack/react-query';
import {useId, useState, type FormEvent} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';

import {PANEL_SNAPSHOT_QUERY_KEY, usePanel} from '../app/PanelProvider.js';
import {registrarSessaoRenovada} from '../auth/session-keeper.js';
import {useWebAuthn} from '../auth/WebAuthnContext.js';
import {cancelamentoDoUsuario} from '../auth/webauthn.js';
import {PANEL_ROOT_ID} from '../components/AppShell.js';
import {Brand} from '../components/Brand.js';
import {isPanelApiError, type AuthSessionDto} from '../data/PanelDataSource.js';

/**
 * Tela de entrada do painel: passkey, e NUNCA senha.
 *
 * A única senha que existe na frente deste painel é a provisória do Caddy, e
 * ela é do navegador com a borda, não desta tela. Aqui a prova é a cerimônia
 * WebAuthn: opções na API, cerimônia no navegador, verificação na API, cookie
 * de sessão na volta.
 *
 * O modo `bootstrap` cadastra a PRIMEIRA passkey usando o convite gerado pelo
 * CLI (`create-owner-enrollment.ts`). O convite entra por campo de formulário
 * e viaja só no corpo do POST: query string fica em histórico, em log de
 * borda e no `Referer`, que são três lugares onde um segredo de uso único não
 * pode aparecer. Ele também nunca encosta em `localStorage`: vive no estado
 * do formulário e é apagado assim que vira cerimônia.
 *
 * Esta tela só existe no caminho HTTP. No modo fixture `dataSource.auth` é
 * `null` e a rota devolve o visitante para `/hoje`, que é o comportamento que
 * a rota desconhecida já tinha antes desta tela existir.
 */

/**
 * Mensagens por código conhecido. Lista de permissão: código novo cai na
 * genérica, e a mensagem é sempre NOSSA, nunca o texto do servidor.
 */
const MENSAGEM_POR_CODIGO: Record<string, string> = {
  OWNER_NOT_FOUND: 'Nenhum owner cadastrado com este e-mail.',
  CHALLENGE_INVALID:
    'A cerimônia venceu ou já foi usada. Comece de novo pelo botão.',
  ASSERTION_INVALID: 'A passkey não foi aceita. Tente de novo.',
  CREDENTIAL_UNKNOWN:
    'Esta passkey não pertence a este painel. Use a passkey cadastrada aqui.',
  CREDENTIAL_COUNTER_REGRESSED:
    'A credencial regrediu o contador, o que pode indicar clone. O acesso foi recusado e o evento ficou registrado.',
  REGISTRATION_INVALID: 'O registro da passkey não foi aceito. Comece de novo.',
  ENROLLMENT_INVALID:
    'Convite inválido, vencido ou já usado. Gere outro no terminal e tente em até 15 minutos.',
  OWNER_ALREADY_EXISTS:
    'O owner já existe. Este cadastro é só da primeira passkey; use a tela de entrar.',
};

const MENSAGEM_POR_STATUS: Record<number, string> = {
  422: 'Os dados não foram aceitos no formato exigido. Confira e tente de novo.',
  429: 'Tentativas demais em pouco tempo. Espere antes de tentar de novo.',
};

const MENSAGEM_GENERICA =
  'Não foi possível entrar. Tente de novo em instantes.';

const descreverProblemaDeEntrada = (erro: unknown): string => {
  if (cancelamentoDoUsuario(erro)) {
    return 'Cerimônia cancelada. Nada foi enviado; tente de novo quando quiser.';
  }
  if (!isPanelApiError(erro)) {
    return MENSAGEM_GENERICA;
  }
  return (
    MENSAGEM_POR_CODIGO[erro.payload.code] ??
    (erro.status !== null ? MENSAGEM_POR_STATUS[erro.status] : undefined) ??
    MENSAGEM_GENERICA
  );
};

/** O mesmo formato mínimo que o serviço da API aceita (`emailValido`). */
const emailValido = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email);

export const EntryPage = ({mode}: {mode: 'login' | 'bootstrap'}) => {
  const {dataSource} = usePanel();
  const webauthn = useWebAuthn();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const campoId = useId();
  const tituloId = useId();

  const [email, setEmail] = useState('');
  const [convite, setConvite] = useState('');
  const [busy, setBusy] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const auth = dataSource.auth;
  const suportado = webauthn.supported();

  if (!auth) {
    // Modo fixture: a porta de login não existe, e esta rota se comporta como
    // qualquer rota desconhecida se comportava antes dela existir.
    return <Navigate to="/hoje" replace />;
  }

  const concluirSessao = (sessao: AuthSessionDto) => {
    // O prazo registrado deixa o guardião agendar a renovação sem precisar
    // girar a sessão recém-nascida. É só carimbo de tempo: nenhum segredo.
    registrarSessaoRenovada(globalThis.localStorage, sessao);
    // `resetQueries`, e não `removeQueries`: o observer do snapshot continua
    // montado no provider, e remover a query debaixo dele a deixa parada sem
    // refetch. O reset zera o 401 antigo E dispara a busca de novo.
    void queryClient.resetQueries({queryKey: PANEL_SNAPSHOT_QUERY_KEY});
    void navigate('/hoje', {replace: true});
  };

  const entrar = async () => {
    const opcoes = await auth.loginOptions(email.trim().toLowerCase());
    if (opcoes.ceremony === 'registration') {
      // Lista de permissão: login é sempre assertion.
      throw new Error('A API devolveu uma cerimônia que não é de login.');
    }
    const resposta = await webauthn.authenticate(opcoes.options);
    return auth.loginVerify({
      ceremonyId: opcoes.ceremonyId,
      response: resposta,
    });
  };

  const cadastrar = async () => {
    const opcoes = await auth.bootstrapOptions(convite.trim());
    // O convite foi consumido pela API na linha acima, com sucesso ou não dali
    // em diante. Apagar já: segredo de uso único gasto não fica em estado de
    // tela esperando um print.
    setConvite('');
    if (opcoes.ceremony !== 'registration') {
      throw new Error('A API devolveu uma cerimônia que não é de cadastro.');
    }
    const resposta = await webauthn.register(opcoes.options);
    return auth.bootstrapVerify({
      ceremonyId: opcoes.ceremonyId,
      response: resposta,
    });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    // Primeira linha, sempre: um submit nativo sem `method` vira GET e joga o
    // campo, inclusive o convite, na query string.
    event.preventDefault();
    if (busy || !suportado) {
      return;
    }

    if (mode === 'login' && !emailValido(email.trim())) {
      setProblema('Informe o e-mail do owner, no formato nome@dominio.');
      return;
    }
    if (mode === 'bootstrap' && convite.trim().length === 0) {
      setProblema('Cole o convite gerado pelo terminal.');
      return;
    }

    setBusy(true);
    setProblema(null);
    void (mode === 'login' ? entrar() : cadastrar())
      .then(concluirSessao)
      .catch((erro: unknown) => {
        setProblema(descreverProblemaDeEntrada(erro));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div id={PANEL_ROOT_ID}>
      <div className="dm-entry">
        <div className="dm-entry-card">
          <Brand />
          <section className="dm-panel" aria-labelledby={tituloId}>
            <div className="dm-panel-head">
              <h1 id={tituloId}>
                {mode === 'login' ? 'Entrar no painel' : 'Cadastro inicial'}
              </h1>
              <span className="dm-illustrative">PASSKEY</span>
            </div>

            <p className="dm-subtitle">
              {mode === 'login'
                ? 'A entrada é por passkey. Não existe senha nesta tela.'
                : 'Primeira passkey do owner, com o convite gerado pelo CLI. O convite vale 15 minutos e é de uso único.'}
            </p>

            {suportado ? null : (
              <div className="dm-alert" role="alert">
                Este navegador não tem suporte a passkeys (WebAuthn). Use um
                navegador atual para entrar no painel.
              </div>
            )}

            <form className="dm-form" onSubmit={submit} noValidate>
              {mode === 'login' ? (
                <div className="dm-field">
                  <label htmlFor={campoId}>E-mail do owner</label>
                  <input
                    id={campoId}
                    type="email"
                    value={email}
                    disabled={busy || !suportado}
                    autoComplete="username"
                    spellCheck={false}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                  />
                </div>
              ) : (
                <div className="dm-field">
                  <label htmlFor={campoId}>Convite de cadastro</label>
                  <input
                    id={campoId}
                    type="text"
                    value={convite}
                    disabled={busy || !suportado}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(event) => {
                      setConvite(event.target.value);
                    }}
                  />
                  <small>
                    Gerado por <code>npm run auth:enroll</code> no servidor. Ele
                    entra aqui, nunca na URL.
                  </small>
                </div>
              )}

              <div className="dm-actions">
                <button
                  className="dm-action dm-action-primary"
                  type="submit"
                  disabled={busy || !suportado}
                >
                  {busy
                    ? 'Aguardando a passkey…'
                    : mode === 'login'
                      ? 'Entrar com passkey'
                      : 'Cadastrar passkey'}
                </button>
              </div>
            </form>

            {problema ? (
              <div className="dm-alert" role="alert">
                {problema}
              </div>
            ) : null}
          </section>

          <p className="dm-entry-note">
            {mode === 'login' ? (
              <Link to="/entrar/cadastro">
                Primeiro acesso? Cadastrar a primeira passkey
              </Link>
            ) : (
              <Link to="/entrar">Já tem passkey? Entrar</Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
